/* eslint-disable react-hooks/exhaustive-deps */
import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Table, Button, message, Modal, Space } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import { parseLocalizedName } from "../../../utils/localization";
import usePermissions from "../../../hooks/usePermissions";
import { filterBySearch } from "../../../shared/lib/listSearch";
import ListSearch from "../../../shared/ui/ListSearch";
import "../../../shared/styles/ErpPages.css";

const GetUnit = () => {
  const { hasPermission } = usePermissions();
  const { t, i18n } = useTranslation();

  const [searchText, setSearchText] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [parentUnits, setParentUnits] = useState({});

  useEffect(() => {
    getApiUnitsWithParents();
  }, [i18n.language]);

  const getApiUnitsWithParents = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/units");
      const unitsData = response.data;

      const unitMap = {};
      unitsData.forEach((unit) => {
        unitMap[unit.id] = unit;
      });

      const fetchedParents = {};
      unitsData.forEach((unit) => {
        if (!unit.parent_id || !unitMap[unit.parent_id]) return;
        fetchedParents[unit.parent_id] = parseLocalizedName(
          unitMap[unit.parent_id].name,
          i18n.language,
          t("N/A")
        );
      });

      setParentUnits(fetchedParents);

      const processedData = unitsData.map((item) => ({
        id: item.id,
        name: parseLocalizedName(item.name, i18n.language, t("N/A")),
        parent_unit_id: item.parent_id,
      }));
      setDataSource(processedData);
    } catch (error) {
      message.error(t("Failed to load units"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmedDelete = async () => {
    try {
      await apiClient.delete(`/units/${deleteItemId}`);
      message.success(t("delete_success"));
      await getApiUnitsWithParents();
      setDeleteItemId(null);
      setIsDeleteModalVisible(false);
    } catch (error) {
      message.error(t("delete_error"));
    }
  };

  const filteredData = useMemo(
    () =>
      filterBySearch(dataSource, searchText, (item) => [
        item.id,
        item.name,
        parentUnits[item.parent_unit_id],
      ]),
    [dataSource, searchText, parentUnits]
  );

  const columns = [
    {
      title: t("id", "ID"),
      dataIndex: "id",
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: t("unit"),
      dataIndex: "name",
      sorter: (a, b) => String(a.name || "").localeCompare(String(b.name || "")),
    },
    {
      title: t("یەکەی سەرەکی"),
      key: "parentUnit",
      render: (_, record) => parentUnits[record.parent_unit_id] || t("N/A"),
    },
    {
      title: t("action", "Action"),
      key: "action",
      width: 160,
      fixed: "right",
      render: (_, record) => (
        <Space size={4}>
          {hasPermission("units.edit") && (
            <Link to={`/GetUnit/UpdateUnit/${record.id}`}>
              <Button size="small" icon={<EditOutlined />}>
                {t("edit", "Edit")}
              </Button>
            </Link>
          )}
          {hasPermission("units.delete") && (
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            >
              {t("delete", "Delete")}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="erp-page">
      <div className="erp-shell">
        <div className="erp-header">
          <div>
            <div className="erp-breadcrumb">
              {t("sidebarCatalog", "Catalog")} / {t("units", "Units")}
            </div>
            <h1>{t("units", "Units")}</h1>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={getApiUnitsWithParents} loading={loading}>
              {t("refresh", "Refresh")}
            </Button>
            {hasPermission("units.add") && (
              <Link to="/AddUnit">
                <Button type="primary" icon={<PlusOutlined />}>
                  {t("addUnit", "New unit")}
                </Button>
              </Link>
            )}
          </Space>
        </div>

        <div className="erp-meta">
          <span>
            {t("records", "Records")}: <b>{dataSource.length}</b>
          </span>
        </div>

        <div className="erp-toolbar">
          <ListSearch
            value={searchText}
            onChange={setSearchText}
            placeholder={t("searchHere")}
            width={280}
          />
        </div>

        <div className="erp-body">
          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={filteredData}
            columns={columns}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: (n) => `${n} ${t("records", "records")}`,
            }}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: t("noDataFound", "No records") }}
          />
        </div>
      </div>

      <Modal
        title={t("confirmDelete")}
        open={isDeleteModalVisible}
        onOk={handleConfirmedDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText={t("confirm")}
        cancelText={t("cancel")}
        okButtonProps={{ danger: true }}
      >
        <p>{t("confirmDeleteMessageUnit")}</p>
      </Modal>
    </div>
  );
};

export default GetUnit;
